import { customElement, html } from '@adobe/edex/ui/edex-lit';
import { EdexTagName, VALID_PAGE_NUMBERS } from '@adobe/edex/ui/shared/constants';
import { PaginationChangePayload } from '@adobe/edex/ui/types/payload';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-left';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-right';

import { BaseElement } from '@adobe/edex/ui/components/base.element';

import styles from '@adobe/edex/ui/components/shared/pagination/pagination.lit.scss';
import { Textfield } from '@spectrum-web-components/textfield';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { $t } from '@adobe/edex/ui/shared/config/i18n';

@customElement(EdexTagName.pagination)
export class Pagination extends BaseElement {
  static styles = styles;
  static get properties() {
    return {
      totalPages: { type: Number },
      page: { type: Number },
    };
  }

  totalPages: number;
  page: number;

  get disablePrevClick() {
    const { page } = this;
    return page <= 1;
  }

  get disableNextClick() {
    const { totalPages, page } = this;
    return page >= totalPages;
  }

  render() {
    const { totalPages, page, disablePrevClick, disableNextClick } = this;

    return html`
      <div class="pagination_main">
        <div
          class="${disablePrevClick ? 'search_prev' : 'search_prev cursor_enabled'}"
          @click=${disablePrevClick ? null : this.handlePrevClick}
        >
          <sp-icon size="m" aria-hidden="true">
            <sp-icon-chevron-left></sp-icon-chevron-left>
          </sp-icon>
        </div>
        <sp-textfield
          class="pagination_textfield"
          .value=${page}
          autocomplete="off"
          @change=${this.handleSubmit}
        ></sp-textfield>

        <span class="pagination_counter">
          ${$t(totalPages === 1 ? I18nKey.paginationSinglePage : I18nKey.paginationMultiPages, { total: totalPages })}
        </span>
        <div
          class="${disableNextClick ? 'search_next' : 'search_next cursor_enabled'}"
          @click=${disableNextClick ? null : this.handleNextClick}
        >
          <sp-icon size="m" aria-hidden="true">
            <sp-icon-chevron-right></sp-icon-chevron-right>
          </sp-icon>
        </div>
      </div>
    `;
  }

  handlePrevClick = () => {
    this.changePage(Math.max(this.page - 1, 1));
  };

  handleNextClick = () => {
    this.changePage(Math.min(this.page + 1, this.totalPages));
  };

  handleSubmit = (e: Event) => {
    const { totalPages } = this;
    const pageValueEntered = e.currentTarget as Textfield;
    if (this.validateInput(pageValueEntered.value)) {
      const pageInput = Number(pageValueEntered.value);
      const page = Math.max(1, Math.min(pageInput, totalPages));
      this.changePage(page);
    }
  };

  private validateInput = (pageValue: string) => {
    return VALID_PAGE_NUMBERS.test(pageValue);
  };

  changePage(page: number) {
    this.emitEvent<PaginationChangePayload>('pageChanged', { page }, { bubbles: true, composed: true });
  }
}
